// Review Persistence Service
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';
import { supabase } from '../config/supabase';

// Removed getSupabaseConfig - now using queuedSupabaseOperation for all database calls

/**
 * Load all movie reviews
 */
export const loadMovieReviews = async () => {
  try {
    console.log('🔍 loadMovieReviews: Starting to load reviews from Supabase...');
    
    const reviews = await queuedSupabaseOperation.select(
      'movie_reviews',
      '*',
      'order=created_at.desc',
      null, // No localStorage fallback for select
      'Load all movie reviews'
    );
    
    console.log('🔍 loadMovieReviews: Raw reviews from Supabase:', reviews);
    console.log('✅ Movie reviews loaded from Supabase:', reviews.length, 'reviews');
    
    // Convert to the format expected by the app (grouped by movie ID)
    const reviewsByMovie = {};
    reviews.forEach(review => {
      if (!reviewsByMovie[review.movie_id]) {
        reviewsByMovie[review.movie_id] = [];
      }
      
      reviewsByMovie[review.movie_id].push({
        name: review.reviewer_name,
        overallRating: review.overall_rating,
        scarinessRating: review.scariness_rating,
        tags: review.tags || [],
        timestamp: review.created_at
      });
    });
    
    console.log('🔍 loadMovieReviews: Processed reviews by movie:', reviewsByMovie);
    console.log('🔍 loadMovieReviews: Movies with reviews:', Object.keys(reviewsByMovie));
    
    // Also save to localStorage as backup
    if (Object.keys(reviewsByMovie).length > 0) {
      localStorage.setItem('movie_reviews', JSON.stringify(reviewsByMovie));
    }
    
    return reviewsByMovie;
  } catch (error) {
    console.error('❌ Error loading movie reviews:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('movie_reviews');
    const fallbackReviews = saved ? JSON.parse(saved) : {};
    console.log('🔄 Fallback to localStorage:', Object.keys(fallbackReviews).length, 'movies with reviews');
    return fallbackReviews;
  }
};

/**
 * Save a movie review
 */
export const saveMovieReview = async (movieId, review) => {
  try {
    // RLS requires a real session to write a review. The request wrapper falls
    // back to the anon key whenever the session is momentarily unreadable -
    // which happens around token refresh - and a write sent that way is
    // refused with 401. Checking here turns a doomed request into an honest
    // error before anything is sent.
    let session = null;
    if (supabase) {
      const { data } = await supabase.auth.getSession();
      session = data?.session || null;
    }
    if (!session?.access_token) {
      return { data: null, error: new Error('You need to be signed in to leave a review') };
    }

    const reviewData = {
      movie_id: movieId,
      reviewer_name: review.name,
      overall_rating: review.overallRating,
      scariness_rating: review.scarinessRating,
      tags: review.tags || [],
      created_at: review.timestamp || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // No localStorage fallback, deliberately. The one that used to be here
    // resolved as SUCCESS when the insert failed, wrote the review to a key
    // nothing ever reads back, and left the caller unable to tell a saved
    // review from a lost one. That is how reviews disappeared silently: the
    // UI said saved, the database never heard about it. A failure must reject.
    const result = await queuedSupabaseOperation.insert(
      'movie_reviews',
      reviewData,
      null,
      `Save movie review: ${movieId} by ${review.name}`
    );

    console.log('✅ Movie review saved to Supabase:', movieId, 'by', review.name);
    return { data: result, error: null };
  } catch (error) {
    console.error('❌ Error saving movie review:', error);
    return { data: null, error };
  }
};

/**
 * Load all movie viewers/statuses
 */
export const loadMovieViewers = async () => {
  try {
    const viewers = await queuedSupabaseOperation.select(
      'movie_viewers',
      '*',
      'order=created_at.desc',
      null, // No localStorage fallback for select
      'Load all movie viewers'
    );
    
    console.log('✅ Movie viewers loaded from Supabase:', viewers.length, 'viewer records');
    
    // Convert to the format expected by the app (grouped by movie ID)
    const viewersByMovie = {};
    viewers.forEach(viewer => {
      if (!viewersByMovie[viewer.movie_id]) {
        viewersByMovie[viewer.movie_id] = [];
      }
      
      viewersByMovie[viewer.movie_id].push({
        name: viewer.viewer_name,
        status: viewer.status,
        ready: viewer.status === 'ready', // Convert status to ready boolean for UI
        joinedAt: viewer.created_at
      });
    });
    
    // Also save to localStorage as backup
    if (Object.keys(viewersByMovie).length > 0) {
      localStorage.setItem('movie_viewers', JSON.stringify(viewersByMovie));
    }
    
    return viewersByMovie;
  } catch (error) {
    console.error('❌ Error loading movie viewers:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('movie_viewers');
    const fallbackViewers = saved ? JSON.parse(saved) : {};
    console.log('🔄 Fallback to localStorage:', Object.keys(fallbackViewers).length, 'movies with viewers');
    return fallbackViewers;
  }
};

/**
 * Save a movie viewer status
 */
export const saveMovieViewer = async (movieId, viewerName, status) => {
  try {
    // First try to update existing record
    try {
      const updateResult = await queuedSupabaseOperation.update(
        'movie_viewers',
        {
          status: status
          // Let database handle updated_at with DEFAULT NOW() or trigger
        },
        `movie_id=eq.${encodeURIComponent(movieId)}&viewer_name=eq.${encodeURIComponent(viewerName)}`,
        null, // No localStorage fallback for update
        `Update movie viewer: ${movieId} - ${viewerName} -> ${status}`
      );
      
      // Check if any rows were actually updated
      if (updateResult && updateResult.length > 0) {
        console.log('✅ Movie viewer updated in Supabase:', movieId, viewerName, status);
        return { data: updateResult, error: null };
      } else {
        // Throw an error to trigger the insert logic
        throw new Error('No rows updated - viewer record does not exist');
      }
    } catch (updateError) {
      // If update fails (record doesn't exist), try insert
      
      const insertResult = await queuedSupabaseOperation.insert(
        'movie_viewers',
        {
          movie_id: movieId,
          viewer_name: viewerName,
          status: status
          // Database will handle created_at and updated_at automatically
        },
        null, // No localStorage fallback for insert
        `Insert movie viewer: ${movieId} - ${viewerName} -> ${status}`
      );
      
      console.log('✅ Movie viewer inserted in Supabase:', movieId, viewerName, status);
      return { data: insertResult, error: null };
    }
  } catch (error) {
    console.error('❌ Error saving movie viewer:', error);
    return { data: null, error };
  }
};

/**
 * Load starred movies for a user
 */
export const loadStarredMovies = async (userIdentifier) => {
  try {
    const data = await queuedSupabaseOperation.select(
      'starred_movies',
      `select=movie_id&user_identifier=eq.${encodeURIComponent(userIdentifier)}`,
      `starred_movies_${userIdentifier}`, // localStorage fallback key
      `Load starred movies for ${userIdentifier}`
    );

    const starredMovieIds = new Set(data.map(s => s.movie_id));
    console.log('✅ Starred movies loaded from Supabase:', starredMovieIds.size, 'movies');
    
    // Also save to localStorage as backup
    localStorage.setItem(`starred_movies_${userIdentifier}`, JSON.stringify(Array.from(starredMovieIds)));
    
    return starredMovieIds;
  } catch (error) {
    console.error('❌ Error loading starred movies:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem(`starred_movies_${userIdentifier}`);
    const fallbackStarred = saved ? new Set(JSON.parse(saved)) : new Set();
    console.log('🔄 Fallback to localStorage:', fallbackStarred.size, 'starred movies');
    return fallbackStarred;
  }
};

/**
 * Toggle starred status for a movie
 */
export const toggleStarredMovie = async (movieId, userIdentifier, isStarred) => {
  try {
    if (isStarred) {
      // Add star
      await queuedSupabaseOperation.insert(
        'starred_movies',
        {
          movie_id: movieId,
          user_identifier: userIdentifier,
          starred_at: new Date().toISOString()
        },
        `starred_movies_${userIdentifier}`, // localStorage fallback key
        `Star movie ${movieId}`
      );
      
      console.log('✅ Movie starred:', movieId);
    } else {
      // Remove star
      await queuedSupabaseOperation.delete(
        'starred_movies',
        `movie_id=eq.${encodeURIComponent(movieId)}&user_identifier=eq.${encodeURIComponent(userIdentifier)}`,
        null, // No localStorage fallback for delete
        `Unstar movie ${movieId}`
      );
      
      console.log('✅ Movie unstarred:', movieId);
    }

    return { error: null };
  } catch (error) {
    console.error('❌ Error toggling starred movie:', error);
    return { error };
  }
};
